import type { CmmdProject } from "@cmmd-forge/contracts";
import { ChevronDownIcon, ChevronRightIcon } from "lucide-react";
import type { MouseEvent } from "react";
import { GithubIcon } from "~/components/icons/GithubIcon";
import { cn } from "~/lib/utils";
import { useCollapsedCmmdWorkspaces } from "./cmmdProjectWorkspaceCollapse";
import {
  resolveGitHubRepositoryIdentity,
  type CmmdProjectWorkspaceGroup,
} from "./CommandPalette.logic";
import { CmmdProjectPickerMetadata } from "./CmmdProjectPickerMetadata";
import { ProjectIdentityIcon } from "./ProjectIdentityIcon";
import { CommandGroup, CommandGroupLabel, CommandItem } from "./ui/command";
import { WorkspaceAvatar } from "./identity/WorkspaceAvatar";

const HEADER_CLASS =
  "flex min-w-0 items-center gap-2 text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground/60";

interface CmmdProjectImportGroupsProps {
  /** `import` shows the project's GitHub repo; `link` shows a Link chip. */
  readonly variant: "import" | "link";
  /** Disambiguates DOM ids and item values between the two pickers. */
  readonly idPrefix: string;
  readonly disabled: boolean;
  readonly groups: readonly CmmdProjectWorkspaceGroup[];
  /**
   * While a query is active every group renders expanded — the rows were
   * already filtered, so a surviving group contains a match and hiding it would
   * make search look broken. This is display-only: the persisted collapse
   * preference is never written, so clearing the query restores it.
   */
  readonly searchActive: boolean;
  readonly onSelectProject: (project: CmmdProject) => void;
  readonly getRowLabel?: ((project: CmmdProject) => string) | undefined;
}

function panelId(idPrefix: string, workspaceId: string): string {
  return `cmmd-ws-panel-${idPrefix}-${workspaceId.replace(/[^a-zA-Z0-9_-]+/g, "-")}`;
}

/**
 * `workspaceIconUrl` is optional per project, so the first row in a group is
 * not reliably the one carrying it — scan the whole group before falling back
 * to the workspace record.
 */
function resolveWorkspaceIconUrl(group: CmmdProjectWorkspaceGroup): string | null {
  for (const project of group.projects) {
    if (project.workspaceIconUrl) return project.workspaceIconUrl;
  }
  return group.workspace?.iconUrl ?? group.workspace?.logoUrl ?? null;
}

export function CmmdProjectImportGroups({
  variant,
  idPrefix,
  disabled,
  groups,
  searchActive,
  onSelectProject,
  getRowLabel,
}: CmmdProjectImportGroupsProps) {
  const { isCollapsed, toggle } = useCollapsedCmmdWorkspaces();

  return groups.map((group) => {
    const collapsed = !searchActive && isCollapsed(group.workspaceId);
    const controlsId = panelId(idPrefix, group.workspaceId);
    const avatar = (
      <WorkspaceAvatar
        color={group.workspace?.color}
        iconUrl={resolveWorkspaceIconUrl(group)}
        logoUrl={group.workspace?.logoUrl ?? null}
        name={group.workspaceName}
        className="inline-flex size-4 shrink-0 items-center justify-center overflow-hidden rounded-sm border border-border/70 bg-muted text-[9px] font-semibold text-muted-foreground"
      />
    );

    return (
      <CommandGroup key={group.workspaceId}>
        {searchActive ? (
          <CommandGroupLabel className={cn(HEADER_CLASS, "px-3 pt-3 pb-1")}>
            {avatar}
            <span className="truncate">{group.workspaceName}</span>
          </CommandGroupLabel>
        ) : (
          <CommandGroupLabel
            render={<button type="button" />}
            // The avatar renders a fallback initial, so name the control
            // explicitly rather than letting it read as "S Second Project".
            aria-label={group.workspaceName}
            aria-controls={controlsId}
            aria-expanded={!collapsed}
            className={cn(
              HEADER_CLASS,
              "w-full rounded-sm px-3 pt-3 pb-1 text-left transition-colors hover:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            )}
            // Keep focus in the command input so arrow keys stay live after a toggle.
            onMouseDown={(event: MouseEvent) => event.preventDefault()}
            onClick={() => toggle(group.workspaceId)}
          >
            {collapsed ? (
              <ChevronRightIcon aria-hidden className="size-3.5 shrink-0" />
            ) : (
              <ChevronDownIcon aria-hidden className="size-3.5 shrink-0" />
            )}
            {avatar}
            <span className="truncate">{group.workspaceName}</span>
          </CommandGroupLabel>
        )}
        {collapsed ? null : (
          // Rows of a collapsed group are unmounted, not hidden — that is what
          // removes them from the Autocomplete composite list, so arrow-key
          // navigation skips them without any index bookkeeping.
          <div id={controlsId} role="none" className="space-y-0.5">
            {group.projects.map((project) => {
              const repository =
                variant === "import"
                  ? resolveGitHubRepositoryIdentity(project.githubRepoUrl)
                  : null;
              return (
                <CommandItem
                  key={`project:${project.workspaceId}:${project.id}`}
                  value={`cmmd-project:${idPrefix}:${project.workspaceId}:${project.id}`}
                  aria-label={getRowLabel?.(project)}
                  className="grid min-h-0 grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 rounded-md px-3 py-2.5 text-left"
                  disabled={disabled}
                  onClick={(event: MouseEvent<HTMLDivElement>) => {
                    // The GitHub link lives inside the row; Base UI defers to
                    // nested anchors and so do we.
                    if ((event.target as HTMLElement).closest("a")) return;
                    onSelectProject(project);
                  }}
                >
                  <ProjectIdentityIcon
                    icon={project.icon}
                    iconUrl={project.iconUrl}
                    projectName={project.title}
                    variant="tile"
                  />
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-semibold">{project.title}</span>
                    <CmmdProjectPickerMetadata projectId={project.id} teamName={project.teamName} />
                  </span>
                  {variant === "link" ? (
                    <span className="inline-flex items-center gap-1.5 self-center rounded-md border border-border/70 px-2 py-1 text-[11px] text-muted-foreground/80">
                      Link
                    </span>
                  ) : repository ? (
                    <a
                      href={repository.url}
                      target="_blank"
                      rel="noreferrer noopener"
                      aria-label={`Open ${repository.label} on GitHub`}
                      className="inline-flex max-w-52 min-w-0 items-center gap-1.5 rounded-md px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <GithubIcon className="size-3.5 shrink-0" />
                      <span className="truncate">{repository.label}</span>
                    </a>
                  ) : (
                    <span />
                  )}
                </CommandItem>
              );
            })}
          </div>
        )}
      </CommandGroup>
    );
  });
}
